import classNames from 'classnames';
import { memo } from 'react';
import { DataTableColumn, SortConfig } from '../../utils/types';
import ColumnHeader from '../ColumnHeader';
import * as styles from '../../styles.module.scss';
import { Z_INDEX } from '../../utils/constants';
import { useTranslation } from '../../../../hooks/useTranslation';

interface TableHeaderProps {
  columns: DataTableColumn[];
  onToggleColumn: (key: string) => void;
  onSort: (key: string) => void;
  sort: SortConfig;
  stickyOffsets: { [index: number]: number };
  headerFixed: boolean;
  enableManageColumn: boolean;
  hasActions: boolean;
}

const TableHeader = memo(
  ({
    columns,
    onToggleColumn,
    onSort,
    sort,
    stickyOffsets,
    headerFixed,
    enableManageColumn,
    hasActions,
  }: TableHeaderProps) => {
    const visibleColumns = columns.filter((col) => !col.isHide);
    const { t } = useTranslation();

    const getHeaderStyle = (column: DataTableColumn, index: number) => {
      const style: React.CSSProperties = {};

      if (
        headerFixed &&
        (column.fixed === 'left' || column.fixed === 'right')
      ) {
        style.zIndex = Z_INDEX.STICKY_HEADER;
      }

      if (column.fixed === 'left') {
        style.left = stickyOffsets[index];
      } else if (column.fixed === 'right') {
        style.right = stickyOffsets[index];
      }

      return style;
    };

    return (
      <thead>
        <tr>
          {visibleColumns.map((column, index) => (
            <th
              key={column.key}
              className={classNames(column.centered && 'text-center', {
                [styles.stickyLeft]: column.fixed === 'left',
                [styles.stickyRight]: column.fixed === 'right',
                [styles.stickyHeader]: headerFixed,
              })}
              style={getHeaderStyle(column, index)}
            >
              <ColumnHeader
                label={column.label}
                columnKey={column.key}
                sortKey={column.sortKey}
                onToggleColumn={onToggleColumn}
                locked={column.locked}
                onSort={onSort}
                sort={sort}
                noSort={column.noSort}
                enableManageColumn={enableManageColumn}
              />
            </th>
          ))}
          {hasActions && (
            <th
              className={classNames({
                [styles.stickyRight]: true,
                [styles.stickyHeader]: headerFixed,
              })}
              style={{
                ...(headerFixed
                  ? { zIndex: Z_INDEX.STICKY_HEADER, top: 0 }
                  : {}),
                right: 0,
              }}
            >
              {t('ACTION')}
            </th>
          )}
        </tr>
      </thead>
    );
  },
);

TableHeader.displayName = 'TableHeader';

export default TableHeader;
